import { getWindow } from "utils-lib/getWindow"

// Example multi-context structure:
// {
//     "user": {
//         "key": "userProfileId",
//         "anonymous": false,
//         "createdAt": new Date().toISOString(), // stored in sessionStorage
//         "email": "user@example.com",
//         "_meta": {
//             "privateAttributes": [
//                 "email",
//                 // add more private attributes here, whatever we want to capture for a user
//             ]
//         }
//     },
//     "session": {
//         "key": "123e4567-e89b-12d3-a456-426614174000", // stored in localStorage to prevent creation of multiple session contexts
//         "anonymous": true,
//         "startedAt": new Date().toISOString(), // stored in sessionStorage
//         "ip": "127.0.0.1"
//     },
//     "application": {
//         "key": "app1234-v1.0",
//         "name": "app",
//         "version": "version",
//         "anonymous": false
//     }
// }

const ssrWindow = getWindow() as Window

const LD_SESSION_ID = `LD_SESSION_ID`
const LD_SESSION_STARTED_AT = "LD_SESSION_STARTED_AT"
const LD_USER_CREATED_AT = "LD_USER_CREATED_AT"
const DEFAULT_SESSION_ID = "default-session-id"

export type UserContext = {
  key: string
  anonymous: boolean
  createdAt: string
  email: string
  _meta: {
    privateAttributes: [
      "email",
      // add more private attributes here, whatever we want to capture for a user
    ]
  }
}

export type SessionContext = {
  key: string
  anonymous: boolean
  startedAt: string
  ip: string | null
}

export type ApplicationContext = {
  key: string
  name: string
  version: string
  anonymous: boolean
}

const generateUniqueId = () => crypto.randomUUID()

const getOrGenerateSessionID = () => {
  try {
    let sessionID = ssrWindow.localStorage.getItem(LD_SESSION_ID) || null

    if (!sessionID) {
      sessionID = generateUniqueId()
      ssrWindow.localStorage.setItem(LD_SESSION_ID, sessionID)
    }

    return sessionID
  } catch {
    // In the case of catostrophic failure, we need to return a default session ID.
    return DEFAULT_SESSION_ID
  }
}

const getOrSetSessionStorageTimeStamp = (key: string): string => {
  try {
    const sessionStartedAt = ssrWindow.sessionStorage.getItem(key) || null

    if (!sessionStartedAt) {
      const now = new Date().toISOString()
      ssrWindow.sessionStorage.setItem(key, now)
      return now
    }

    return sessionStartedAt
  } catch (error) {
    console.error(
      `Error accessing session storage for key "${key}. Creating a new timestamp."`,
      error,
    )
    const now = new Date().toISOString()
    return now
  }
}

/**
 * Maps a partial `UserContext` object to a complete `UserContext` object.
 * Ensures that the required `key` property is present and assigns default values
 * to other properties if they are not provided.
 *
 * @param user - A partial `UserContext` object containing user information.
 * @returns A fully constructed `UserContext` object.
 * @throws {Error} If the `key` property is missing from the input `user` object.
 *
 * The returned `UserContext` object includes:
 * - `key`: The key from the provided user object.
 * - `anonymous`: Always set to `false`.
 * - `createdAt`: The current timestamp in ISO 8601 format.
 * - `privateAttributes`: The private attributes from the provided user object, or an empty object if not provided.
 */
export const mapUserContext = (user: Partial<UserContext>): UserContext => {
  if (!user.key) {
    throw new Error("User key is required")
  }

  return {
    key: user.key,
    anonymous: false,
    createdAt: getOrSetSessionStorageTimeStamp(LD_USER_CREATED_AT),
    email: user.email || "",
    _meta: {
      privateAttributes: [
        "email",
        // add more private attributes here, whatever we want to capture for a user
      ],
    },
  }
}

/**
 * Maps a partial session context to a complete `SessionContext` object.
 *
 * @param session - A partial session context object containing optional properties.
 * @returns A fully constructed `SessionContext` object.
 * @throws An error if the `session.key` property is not provided.
 *
 * The returned `SessionContext` object includes:
 * - `key`: The key from the provided session object.
 * - `anonymous`: Always set to `true`.
 * - `ip`: The IP address from the session object, or an empty string if not provided.
 * - `startedAt`: The current timestamp in ISO 8601 format.
 */
export const mapSessionContext = (
  session: Partial<SessionContext>,
): SessionContext => ({
  key: getOrGenerateSessionID(),
  anonymous: true,
  ip: session.ip || null,
  startedAt: getOrSetSessionStorageTimeStamp(LD_SESSION_STARTED_AT),
})

/**
 * Maps a partial `ApplicationContext` object to a complete `ApplicationContext` object.
 *
 * @param application - A partial representation of the `ApplicationContext` object.
 *   - `key` (required): A unique identifier for the application. Throws an error if not provided.
 *   - `name` (optional): The name of the application. Defaults to an empty string if not provided.
 *   - `version` (optional): The version of the application. Defaults to an empty string if not provided.
 *
 * @returns A complete `ApplicationContext` object with the provided or default values.
 *
 * @throws {Error} If the `key` property is not provided in the `application` parameter.
 *
 * The returned `ApplicationContext` object includes:
 * - `key`: The key from the provided application object.
 * - `name`: The name from the provided application object, or an empty string if not provided.
 * - `version`: The version from the provided application object, or an empty string if not provided.
 * - `anonymous`: Always set to `false`.
 */
export const mapApplicationContext = (
  application: Partial<ApplicationContext>,
): ApplicationContext => {
  if (!application.key) {
    throw new Error("Application key is required")
  }

  return {
    key: application.key,
    name: application.name || "",
    version: application.version || "",
    anonymous: false,
  }
}
